import { useState } from "react";
import { Send } from "lucide-react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  CONTACT_ROUTES,
  contactRequestSchema,
  contactResponseSchema,
  type ContactReceipt,
  type ContactRequest,
} from "@nh/shared";
import {
  Alert,
  EmailField,
  Form,
  SubmitButton,
  TextField,
  TextareaField,
} from "@/shared/ui/core";
import { apiFetch, isApiError } from "@/shared/api/client";

/**
 * El formulario de contacto de la tienda.
 *
 * ── Lo que este archivo hacía antes, y por qué era grave ─────────────
 * `handleSubmit` limpiaba los tres campos y pintaba "Recibimos tu
 * mensaje. Te responderemos muy pronto." sin llamar a NADIE. No había
 * endpoint, así que el mensaje de esa persona no se perdía por un error:
 * no existía en ningún lado desde el primer instante. Quien escribía se
 * quedaba esperando una respuesta que nadie iba a poder dar, porque nadie
 * llegó a leer su pregunta.
 *
 * Ahora hay `POST /api/contact`, que GUARDA el mensaje en la base antes de
 * intentar el correo de aviso. Por eso la confirmación solo aparece cuando
 * el servidor contestó: ya no es una promesa, es un acuse.
 *
 * ── Valida el mismo esquema que el servidor ──────────────────────────
 * `contactRequestSchema` de `@nh/shared`, enchufado con `zodResolver`,
 * igual que el login y el registro. Antes eran tres `useState` sueltos con
 * la validación del navegador, y eso significaba que el correo se
 * comprobaba aquí con una regla distinta a la del resto del sistema — un
 * correo que el alta rechaza podía entrar por aquí, y al revés. De paso
 * normaliza: el correo viaja en minúsculas y sin espacios.
 *
 * `<Form>` pinta `noValidate`, así que las burbujas del navegador no
 * aparecen: los errores se ven en el campo, en español, como en todo el
 * sistema.
 */

/** `CONTACT_ROUTES.send` ya trae el prefijo `/api`, de ahí el `baseUrl: ""`. */
function sendContactMessage(input: ContactRequest): Promise<ContactReceipt> {
  return apiFetch(CONTACT_ROUTES.send, contactResponseSchema, {
    baseUrl: "",
    method: "POST",
    body: input,
  });
}

const EMPTY: ContactRequest = { name: "", email: "", message: "" };

export function ContactForm() {
  const { t } = useTranslation();

  const form = useForm<ContactRequest>({
    resolver: zodResolver(contactRequestSchema),
    defaultValues: EMPTY,
    mode: "onSubmit",
  });

  /** `true` solo cuando el SERVIDOR confirmó que guardó el mensaje. */
  const [sent, setSent] = useState(false);
  /**
   * El último envío que falló, o `null`.
   *
   * `detail` es lo que dijo el servidor —"intenta en una hora", "no se
   * pudo contactar al servidor"—, y es `null` cuando lo que falló no
   * trajo un mensaje que sirva de algo. Sea cual sea, debajo va SIEMPRE la
   * salida: el correo directo. Un error sin alternativa deja a alguien
   * atrapado con su pregunta.
   */
  const [failure, setFailure] = useState<{ detail: string | null } | null>(null);

  const submit = async (values: ContactRequest) => {
    setFailure(null);
    setSent(false);

    try {
      await sendContactMessage(values);
      // Se limpia DESPUÉS de la confirmación, no antes. Si algo falla, lo
      // que la persona escribió sigue en la caja y puede reintentar sin
      // volver a teclearlo — que es exactamente lo que el formulario viejo
      // hacía mal: borraba primero y no mandaba nunca.
      form.reset(EMPTY);
      setSent(true);
    } catch (error) {
      /*
       * Se conserva el mensaje del servidor cuando lo hay: el límite por
       * IP dice cuánto esperar, y eso es más útil que cualquier frase de
       * la casa. Lo que NO se hace es ramificar sobre el texto —para eso
       * está `error.code`— ni dejar que sea lo único que se lee.
       */
      setFailure({ detail: isApiError(error) ? error.message : null });
    }
  };

  return (
    <Form
      form={form}
      onSubmit={submit}
      className="rounded-ds-xl border border-ds-border bg-white/80 p-6 shadow-ds-card sm:p-8"
    >
      <div className="grid gap-4">
        <TextField
          name="name"
          label={t("Nombre")}
          placeholder={t("Tu nombre")}
          autoComplete="name"
          required
        />

        {/*
          `purpose="contact"` (el valor por omisión) y no `"account"`: este
          correo es un dato para contestarle a alguien, no la llave con la
          que entra a una cuenta. La diferencia la paga quien usa un gestor
          de contraseñas — ver `EmailField`.
        */}
        <EmailField
          label={t("Correo")}
          placeholder="tu@correo.com"
          required
        />

        <TextareaField
          name="message"
          label={t("Mensaje")}
          placeholder={t("Escribe tu mensaje…")}
          hint={t("¿En qué podemos ayudarte?")}
          rows={4}
          required
        />

        {/* Los dos avisos son excluyentes: `submit` apaga uno al encender
            el otro, para que nunca se lean juntos y contradiciéndose. */}
        {sent && (
          <Alert variant="success" onDismiss={() => setSent(false)}>
            {t("Recibimos tu mensaje. Te responderemos muy pronto.")}
          </Alert>
        )}

        {failure && (
          <Alert
            variant="error"
            title={t("No pudimos enviar tu mensaje")}
            onDismiss={() => setFailure(null)}
          >
            {failure.detail && <p>{failure.detail}</p>}
            {/* La salida va SIEMPRE, falle lo que falle: el correo directo
                está a la izquierda de esta misma sección, pero quien acaba
                de ver un error no debería tener que ir a buscarlo. */}
            <p className={failure.detail ? "mt-1" : undefined}>
              {t(
                "Vuelve a intentarlo en un momento o escríbenos directo a hola@itsthebest1111.com.",
              )}
            </p>
          </Alert>
        )}

        {/*
          El estado "enviando" lo pinta `SubmitButton` solo: saca
          `isSubmitting` del formulario y pone el spinner, y los tres
          campos se deshabilitan por su cuenta —lo hace `useFieldConnection`—
          así que nadie puede editar ni enviar dos veces.

          El `aria-label` tapa un hueco real: mientras carga, `Button`
          esconde su contenido con `invisible` para no cambiar de ancho, y
          eso lo saca también del árbol de accesibilidad. Sin esta línea,
          quien usa lector de pantalla se queda con un botón sin nombre
          justo en el momento en que más falta hace saber qué está pasando.
        */}
        <SubmitButton
          variant="dark"
          fullWidth
          leftIcon={<Send />}
          {...(form.formState.isSubmitting ? { "aria-label": t("Enviando…") } : {})}
        >
          {t("Enviar mensaje")}
        </SubmitButton>
      </div>
    </Form>
  );
}
